import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatShortcut, Select } from '@/components/ui';
import { UserMenu } from '@/components/UserMenu';
import { CommandPalette } from '@/components/shell/CommandPalette';
import { NotificationPopover } from '@/components/shell/NotificationPopover';
import { useCommandPalette } from '@/components/shell/useCommandPalette';
import cmdkStyles from '@/components/shell/CommandPalette.module.css';
import { useConsoleBreadcrumbItems } from '@/components/shell/ConsoleBreadcrumbContext';
import { useTheme } from '@/theme/useTheme';
import { useLanguage, type PortalLanguage } from '@/i18n/useLanguage';

const LANGUAGE_OPTIONS: { value: PortalLanguage; label: string }[] = [
    { value: 'vi', label: 'Tiếng Việt' },
    { value: 'en', label: 'English' },
];

/**
 * EAA-1813 (canonical shell — handoff frame 09 "Inset shell home"): the
 * console's in-panel topbar.
 *
 * Deliberately its own component rather than a reuse of the shared `AppBar`
 * (still used by the launcher and profile pages): frame 09's topbar
 * sits INSIDE the rounded content panel, is only as wide as the panel, and
 * carries no brand/logo — the sidebar's org-switcher already carries that
 * identity, so repeating it here drew the mark twice.
 *
 * EAA-1846 (frame 95 "Vỏ chuẩn", reverses the paragraph above for its OWN
 * claim): the collapse toggle that lived here moved to the bottom of
 * `ConsoleNav` — "thanh trên thuộc về trang, rail thuộc về điều hướng" (the
 * topbar belongs to the page, the rail belongs to navigation). This
 * component now renders the per-route breadcrumb (`ConsoleBreadcrumbContext`)
 * where that button used to be, immediately left of the search box.
 *
 * EAA-1813 (frames 13 + 14): search and the bell are LIVE now. Both shipped
 * `disabled` — unfocusable, out of the tab order, with an sr-only "chưa khả
 * dụng" note — purely because no endpoint existed; EAA-1831 added
 * `/api/v1/search` and `/api/v1/me/notifications*`, so the `disabled`
 * attributes and those sr-only notes are gone and the real controls replace
 * them. The search box is a `<button>` rather than an `<input>` now: it opens
 * the ⌘K dialog, which owns the actual query field (frame 13), so a text input
 * here would have been a decoy with nothing to type into.
 *
 * The bell's unread dot from the frame is now a real count badge fed by
 * `/me/notifications/summary`, and renders only when that count is non-zero —
 * the earlier objection (a permanent dot asserting unread state with no data
 * behind it) no longer applies.
 *
 * EAA-1844: the frame's settings/theme glyph now renders too — tokens.css got
 * a `[data-theme="dark"]` override block, so there is a real theme to switch
 * (the earlier "no dark-theme token layer" reason this stayed unrendered no
 * longer holds). It is a light/dark toggle wired to `useTheme()`, not a
 * settings menu — the frame's single glyph covers both concepts and only the
 * theme half has a destination today.
 *
 * EAA-1845: a vi/en language dropdown sits next to it, wired to
 * `useLanguage()`. Built on the kit's `Select` (`@ai-content-hub/ui`, via the
 * `@/components/ui` barrel) rather than a raw `<select>` or a toggle button —
 * every native form control in this console was migrated onto the kit for
 * exactly this reason (see the barrel's own EAA-1826 comment on `Select`),
 * and it gets dark-theme colouring for free through `createAntdTheme` instead
 * of a hand-maintained chevron/border CSS pass.
 */
export function ConsoleTopbar() {
    const { t } = useTranslation();
    const palette = useCommandPalette();
    const { theme, toggleTheme } = useTheme();
    const { language, setLanguage } = useLanguage();
    const breadcrumb = useConsoleBreadcrumbItems();

    return (
        <header className="portal-console-topbar">
            {breadcrumb && breadcrumb.length > 0 && (
                <nav
                    className="portal-console-topbar-breadcrumb"
                    aria-label={t('shell.topbar.breadcrumbAria')}
                >
                    {breadcrumb.map((item, index) => {
                        const isLast = index === breadcrumb.length - 1;
                        return (
                            <span key={`${item.label}-${index}`} className="portal-console-topbar-breadcrumb__item">
                                {item.to && !isLast ? (
                                    <Link to={item.to}>{item.label}</Link>
                                ) : (
                                    <span
                                        aria-current={isLast ? 'page' : undefined}
                                        className={
                                            isLast
                                                ? 'portal-console-topbar-breadcrumb__current'
                                                : undefined
                                        }
                                    >
                                        {item.label}
                                    </span>
                                )}
                                {!isLast && <span aria-hidden="true">/</span>}
                            </span>
                        );
                    })}
                </nav>
            )}
            <button
                type="button"
                className={`portal-console-topbar-search ${cmdkStyles.trigger}`}
                onClick={palette.openPalette}
                aria-haspopup="dialog"
                aria-keyshortcuts="Meta+K Control+K"
                aria-label={t('shell.search.ariaLabel')}
            >
                <SearchIcon />
                <span className={cmdkStyles.triggerLabel}>{t('shell.search.triggerLabel')}</span>
                {/* EAA-1826: per-platform — see AppBar for why the literal ⌘K
                    was wrong on Windows/Linux. */}
                <span className="portal-console-topbar-kbd" aria-hidden="true">
                    {formatShortcut('K')}
                </span>
            </button>
            <span className="portal-console-topbar-spacer" />
            <button
                type="button"
                className="portal-console-topbar-icon-btn portal-console-topbar-theme"
                onClick={toggleTheme}
                aria-pressed={theme === 'dark'}
                aria-label={
                    theme === 'dark' ? t('shell.topbar.themeToLight') : t('shell.topbar.themeToDark')
                }
                title={theme === 'dark' ? t('shell.topbar.themeToLight') : t('shell.topbar.themeToDark')}
            >
                {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
            </button>
            {/* EAA-1845: language dropdown. Each option is labelled in ITS OWN
                language ("Tiếng Việt" / "English"), not translated into the
                currently active one, so a reader who can't read the current
                language can still find their own in the list — the same
                reasoning most native language pickers follow. */}
            <Select<PortalLanguage>
                className="portal-console-topbar-lang-select"
                value={language}
                onChange={setLanguage}
                options={LANGUAGE_OPTIONS}
                aria-label={t('shell.topbar.langSelectAriaLabel')}
                popupMatchSelectWidth={false}
            />
            <NotificationPopover
                triggerClassName="portal-console-topbar-icon-btn portal-console-topbar-bell"
                iconSize={18}
            />
            <span className="portal-console-topbar-user">
                <UserMenu />
            </span>
            {palette.open && <CommandPalette onClose={palette.closePalette} />}
        </header>
    );
}

/* Inline icons — no icon dependency (design-pass rule), matching every other
   nav/appbar icon in this repo. EAA-1846: the collapse toggle (and its
   PanelCollapseIcon) moved to ConsoleNav — see that file's RailExpandIcon/
   RailCollapseIcon for its replacements. */

/* EAA-1844: sun/moon glyph for the theme toggle — same inline-icon
   convention as SearchIcon below (no icon dependency). */
function SunIcon() {
    return (
        <svg
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            aria-hidden="true"
        >
            <circle cx="12" cy="12" r="4" />
            <path
                d="M12 2v2M12 20v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M2 12h2M20 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4"
                strokeLinecap="round"
            />
        </svg>
    );
}

function MoonIcon() {
    return (
        <svg
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            aria-hidden="true"
        >
            <path
                d="M20 14.5A8.5 8.5 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5Z"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

function SearchIcon() {
    return (
        <svg
            viewBox="0 0 24 24"
            width="15"
            height="15"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            aria-hidden="true"
        >
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" strokeLinecap="round" />
        </svg>
    );
}
